import React, { useRef, useEffect, useState } from "react";
import { MdUpload } from "react-icons/md";
import { RiDeleteBin2Fill } from "react-icons/ri";
import { maxLogoSize, logoFormat } from "../utils";
import useGetLanguage from "../../../../utils/useGetLanguage";
import { LanguageText } from "../../../../dictionary/GroupsText";

interface LogoProps {
  src?: File | string | null;
  onChange: (file: File) => void;
  onDelete: () => void;
}

const Logo: React.FC<LogoProps> = ({ src, onChange, onDelete }) => {
  const language: LanguageText = useGetLanguage({
    fileName: "GroupsText",
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const [imgSrc, setImgSrc] = useState<string | null>(null);

  // Only create/revoke object URLs when src changes and is a File
  useEffect(() => {
    if (!src) {
      setImgSrc(null);
      return;
    }
    if (typeof src === "string") {
      setImgSrc(src);
      return;
    }
    // src is a File
    const objectUrl = URL.createObjectURL(src);
    setImgSrc(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [src]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > maxLogoSize) alert("File too big. 5MB MAX");
      if (!logoFormat.includes(file.type)) alert("Not the right format");
      onChange(file);
    }
  };
  const handleDelete = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    onDelete();
  };

  return (
    <div className="w-full max-w-full min-w-0 bg-white rounded-md sm:rounded-lg p-1.5 sm:p-2 md:p-3 relative">
      <label className="block text-xs sm:text-sm font-medium mb-1 sm:mb-1.5">
        {language?.groupStyle?.components?.logo?.title || "Logo"}
      </label>
      <div className="relative w-full min-w-0 flex justify-center">
        <div className="relative w-full max-w-[120px] sm:max-w-[150px] md:max-w-[180px] min-w-0">
          <div className="absolute right-1 top-1 sm:right-1.5 sm:top-1.5 flex gap-1 sm:gap-1.5 z-10">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="bg-white rounded-full p-0.5 sm:p-1 shadow hover:bg-gray-100 transition-colors"
              aria-label="Upload logo"
            >
              <MdUpload size={12} className="sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
            </button>
            {src && (
              <button
                type="button"
                onClick={handleDelete}
                className="bg-white rounded-full p-0.5 sm:p-1 shadow hover:bg-gray-100 transition-colors"
                aria-label="Delete logo"
              >
                <span
                  role="img"
                  aria-label="delete"
                  className="text-black hover:text-red-500 transition-colors"
                >
                  <RiDeleteBin2Fill size={12} className="sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
                </span>
              </button>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept={logoFormat.join(",")}
            className="hidden"
            onChange={handleFileChange}
          />
          <div className="w-full max-w-full min-w-0 aspect-square rounded-md sm:rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center overflow-hidden">
            {imgSrc ? (
              <img 
                src={imgSrc} 
                alt="Site Logo" 
                className="object-contain w-full h-full max-w-full max-h-full" 
              />
            ) : (
              <span className="text-xl sm:text-2xl md:text-3xl text-gray-400">📸</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
export default Logo;
